import type { GlobalThemeOverrides } from 'naive-ui'

// "Graphite & Signal" — the JS mirror of the `@theme` block in
// `src/style.css`. Naive UI can't read CSS custom properties at theme
// build time, so the values live here once and App.vue hands
// `naiveThemeOverrides` to `NConfigProvider`. `tokens.spec.ts` asserts
// every entry matches the stylesheet, so a palette change in one place
// without the other fails CI instead of drifting silently.
export const colorTokens = {
  'brand-primary': '#5fd4a8',
  factory: '#e8a65a',
  life: '#b9a3f0',
  attention: '#7aa7f0',
  'status-alert': '#f08a7e',
  'surface-bg': '#0e0f11',
  'surface-rail': '#121316',
  'surface-raised': '#16181b',
  'surface-hover': '#1f2226',
  'border-subtle': '#2a2e33',
  'border-divider': '#23262b',
  'border-control': '#646a72',
  ink: '#ecedee',
  'ink-reading': '#dadcdf',
  'ink-muted': '#a3a8af',
  'ink-faint': '#868c94',
} as const

export type ColorToken = keyof typeof colorTokens

export const fontStacks = {
  sans: "'Geist Variable', ui-sans-serif, system-ui, sans-serif",
  serif: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
  mono: "'Geist Mono Variable', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const

// Hover/pressed shades of the mint primary — Naive needs explicit
// variants rather than deriving them.
const PRIMARY_HOVER = '#7fe0bb'
const PRIMARY_PRESSED = '#46b98f'
const CONTROL_RADIUS = '10px'

const c = colorTokens

export const naiveThemeOverrides: GlobalThemeOverrides = {
  common: {
    fontFamily: fontStacks.sans,
    fontFamilyMono: fontStacks.mono,
    primaryColor: c['brand-primary'],
    primaryColorHover: PRIMARY_HOVER,
    primaryColorPressed: PRIMARY_PRESSED,
    primaryColorSuppl: c['brand-primary'],
    infoColor: c.attention,
    warningColor: c.factory,
    errorColor: c['status-alert'],
    successColor: c['brand-primary'],
    bodyColor: c['surface-bg'],
    cardColor: c['surface-raised'],
    modalColor: c['surface-raised'],
    popoverColor: c['surface-raised'],
    tableColor: c['surface-raised'],
    inputColor: c['surface-bg'],
    borderColor: c['border-control'],
    dividerColor: c['border-divider'],
    textColorBase: c.ink,
    textColor1: c.ink,
    textColor2: c['ink-reading'],
    textColor3: c['ink-muted'],
    placeholderColor: c['ink-faint'],
    hoverColor: c['surface-hover'],
    borderRadius: CONTROL_RADIUS,
  },
  Button: {
    textColorPrimary: '#0b1f17',
    textColorHoverPrimary: '#0b1f17',
    textColorPressedPrimary: '#0b1f17',
    textColorFocusPrimary: '#0b1f17',
    fontWeight: '500',
  },
  Card: {
    borderColor: c['border-subtle'],
    borderRadius: '16px',
  },
  Drawer: {
    color: c['surface-raised'],
  },
  // Segmented tabs (Reminders' One-time / Schedules, the Add-to-brain
  // drawer's Note / File / URL): a recessed track with a raised pill.
  Tabs: {
    colorSegment: c['surface-bg'],
    tabColorSegment: c['surface-hover'],
    tabTextColorActiveSegment: c.ink,
    tabTextColorSegment: c['ink-muted'],
  },
}
